import {
	DcrFieldName,
	type DcrhcpInput,
	type Hcp,
	ReferenceType
} from '@/core/graphql/generated/types';
import { Permission } from '@/core/store/auth/auth.types';
import { getPrimaryAddress } from '@/utils/addresses/getPrimaryAddress';
import { formatPhoneNumber } from '@/utils/formatters/formatPhoneNumber';
import { getLocalSpecialtyValue } from '@/utils/specialty/getLocalSpecialtyValue';
import { validateNotUnknown } from '@/utils/validateNotUnknown';

import {
	EMAILS_FIELD_NAME,
	inlineCollection,
	inlineEmails,
	inlineField,
	inlineName,
	inlineStatus,
	NAME_GROUP_KEY,
	PROFILE_STATUS_FIELD_KEY
} from './inline';
import { labelKey as t } from './labelKey';
import type { FieldDef, HistoricFieldDef, InlineLayout } from './types';

export const LOCAL_SPECIALTY_MAX_ITEMS = 10;
export const NON_PRIMARY_LOCAL_SPECIALTY_IDS = ['USNPS'];

const US_EMAIL = {
	kind: 'email',
	section: 'contact',
	visibility: 'addable',
	groupWith: 'emailCda_V',
	countries: ['us'],
	permission: Permission.UsEmails
} as const;

export const HCP_FIELDS = {
	firstNameCda_V: {
		label: t('First Name'),
		section: 'name',
		kind: 'text',
		visibility: 'always',
		required: true,
		enumNames: [DcrFieldName.FirstNameCdaV]
	},
	middleNameCda_V: {
		label: t('Middle Name'),
		section: 'name',
		kind: 'text',
		visibility: 'addable',
		enumNames: [DcrFieldName.MiddleNameCdaV]
	},
	lastNameCda_V: {
		label: t('Last Name'),
		section: 'name',
		kind: 'text',
		visibility: 'always',
		required: true,
		enumNames: [DcrFieldName.LastNameCdaV]
	},
	prefixCda_V: {
		label: t('Prefix'),
		section: 'name',
		kind: 'text',
		visibility: 'addable',
		enumNames: [DcrFieldName.PrefixCdaV]
	},
	suffixCda_V: {
		label: t('Suffix'),
		section: 'name',
		kind: 'text',
		visibility: 'addable',
		enumNames: [DcrFieldName.SuffixCdaV]
	},
	internationalName_V: {
		label: t('International Name'),
		section: 'name',
		kind: 'text',
		visibility: 'off',
		enumNames: [DcrFieldName.InternationalNameV],
		backendNames: ['internationalNameCda_V']
	},

	primaryCountry_V: {
		label: t('Country'),
		section: 'professional',
		kind: 'picklist',
		picklist: 'country',
		visibility: 'off',
		enumNames: [DcrFieldName.PrimaryCountryV]
	},
	hcpTypeCda_VId: {
		label: t('Global HCP Type'),
		section: 'professional',
		kind: 'picklist',
		picklist: ReferenceType.HcpType,
		visibility: 'always',
		required: true,
		enumNames: [DcrFieldName.HcpTypeCdaVId],
		backendNames: ['hcpType']
	},
	hcpTypeLocal_VId: {
		label: t('Local HCP Type'),
		section: 'professional',
		kind: 'picklist',
		picklist: ReferenceType.LocalHcpType,
		visibility: 'addable',
		enumNames: [DcrFieldName.HcpTypeLocalVId]
	},
	degree1Cda_VId: {
		label: t('Medical Degree'),
		section: 'professional',
		kind: 'picklist',
		picklist: ReferenceType.MedicalDegree,
		visibility: 'addable',
		enumNames: [DcrFieldName.Degree_1CdaVId],
		backendNames: ['allDegreeCda_V']
	},
	spec1Cda_VId: {
		label: t('Global Specialty'),
		section: 'professional',
		kind: 'picklist',
		picklist: ReferenceType.Specialty,
		visibility: 'off',
		enumNames: [DcrFieldName.Spec_1CdaVId],
		backendNames: ['hcpSpecialties']
	},
	allSpecLocal_VId: {
		label: t('Local Specialties'),
		section: 'professional',
		kind: 'multiPicklist',
		picklist: ReferenceType.SpecialtyLocal,
		visibility: 'always',
		required: true,
		maxItems: LOCAL_SPECIALTY_MAX_ITEMS,
		primaryExcludedOptionIds: NON_PRIMARY_LOCAL_SPECIALTY_IDS,
		enumNames: [
			DcrFieldName.AllSpecLocalVId,
			DcrFieldName.Spec_1LocalVId,
			DcrFieldName.Spec_2LocalVId
		],
		backendNames: ['localSpecialties']
	},
	statusCda_VId: {
		label: t('Status'),
		section: 'professional',
		kind: 'picklist',
		picklist: ReferenceType.Status,
		visibility: 'addable',
		enumNames: [DcrFieldName.StatusCdaVId]
	},
	hcpStatus_VId: {
		label: t('Status'),
		section: 'professional',
		kind: 'picklist',
		picklist: ReferenceType.HcpStatus,
		visibility: 'off',
		enumNames: [DcrFieldName.HcpStatusVId]
	},
	prescriberCda_V: {
		label: t('Prescriber'),
		section: 'professional',
		kind: 'boolean',
		visibility: 'off',
		enumNames: [DcrFieldName.PrescriberCdaV]
	},
	telehealth_V: {
		label: t('Telehealth'),
		section: 'professional',
		kind: 'boolean',
		visibility: 'off',
		enumNames: [DcrFieldName.TelehealthV]
	},
	inCurrentPecos_V: {
		label: t('In Current PECOS'),
		section: 'professional',
		kind: 'boolean',
		visibility: 'off',
		enumNames: [DcrFieldName.InCurrentPecosV]
	},

	nhidCda_V: {
		label: t('National Healthcare ID'),
		section: 'ids',
		kind: 'text',
		visibility: 'addable',
		enumNames: [DcrFieldName.NhidCdaV]
	},
	nhid2_V: {
		label: t('Secondary Healthcare ID'),
		section: 'ids',
		kind: 'text',
		visibility: 'off',
		enumNames: [DcrFieldName.Nhid_2V]
	},
	hcpTaxId_V: {
		label: t('Tax ID'),
		section: 'ids',
		kind: 'text',
		visibility: 'off',
		enumNames: [DcrFieldName.HcpTaxIdV]
	},

	languageCda_VId: {
		label: t('Language'),
		section: 'demographics',
		kind: 'picklist',
		picklist: ReferenceType.Language,
		visibility: 'off',
		enumNames: [DcrFieldName.LanguageCdaVId]
	},
	gender_VId: {
		label: t('Gender'),
		section: 'demographics',
		kind: 'picklist',
		picklist: ReferenceType.HcpGender,
		visibility: 'addable',
		enumNames: [DcrFieldName.GenderVId]
	},
	birthYear_V: {
		label: t('Year of Birth'),
		section: 'demographics',
		kind: 'year',
		visibility: 'addable',
		enumNames: [DcrFieldName.BirthYearV, DcrFieldName.YearOfBirthCdaV],
		backendNames: ['yearOfBirthCda_V']
	},
	dataPrivacyOptOut_V: {
		label: t('OpenData Data Privacy Opt Out'),
		section: 'demographics',
		kind: 'boolean',
		visibility: 'off',
		enumNames: [DcrFieldName.DataPrivacyOptOutV]
	},
	gradSchool_V: {
		label: t('Graduation School'),
		section: 'demographics',
		kind: 'text',
		visibility: 'off',
		enumNames: [DcrFieldName.GradSchoolV]
	},
	gradYear_V: {
		label: t('Graduation Year'),
		section: 'demographics',
		kind: 'year',
		visibility: 'off',
		enumNames: [DcrFieldName.GradYearV]
	},

	officePhoneCda_V: {
		label: t('Office Phone'),
		section: 'contact',
		kind: 'phone',
		visibility: 'off',
		enumNames: [DcrFieldName.OfficePhoneCdaV]
	},
	phone2_V: {
		label: t('Phone 2'),
		section: 'contact',
		kind: 'phone',
		visibility: 'off',
		enumNames: [DcrFieldName.Phone_2V]
	},
	faxCda_V: {
		label: t('Fax'),
		section: 'contact',
		kind: 'phone',
		visibility: 'off',
		enumNames: [DcrFieldName.FaxCdaV]
	},
	emailCda_V: {
		label: t('Email'),
		labelByCountry: { us: t('Email {{number}}', { number: 1 }) },
		permissionByCountry: { us: Permission.UsEmails },
		section: 'contact',
		kind: 'email',
		visibility: 'addable',
		enumNames: [DcrFieldName.EmailCdaV],
		backendNames: ['email']
	},
	email2_V: {
		...US_EMAIL,
		label: t('Email {{number}}', { number: 2 }),
		enumNames: [DcrFieldName.Email_2V],
		backendNames: ['email2']
	},
	email3_V: {
		...US_EMAIL,
		label: t('Email {{number}}', { number: 3 }),
		enumNames: [DcrFieldName.Email_3V],
		backendNames: ['email3']
	},
	email4_V: {
		...US_EMAIL,
		label: t('Email {{number}}', { number: 4 }),
		enumNames: [DcrFieldName.Email_4V],
		backendNames: ['email4']
	},
	email5_V: {
		...US_EMAIL,
		label: t('Email {{number}}', { number: 5 }),
		enumNames: [DcrFieldName.Email_5V],
		backendNames: ['email5']
	},
	websiteCda_V: {
		label: t('Website'),
		section: 'contact',
		kind: 'url',
		visibility: 'addable',
		enumNames: [DcrFieldName.WebsiteCdaV, DcrFieldName.Url_1V],
		backendNames: ['URL1_V', 'website_V']
	},

	parentHcos_V: {
		label: t('Affiliations'),
		section: 'affiliations',
		kind: 'collection',
		collection: 'affiliation',
		visibility: 'always',
		enumNames: [DcrFieldName.ParentHcosV],
		backendNames: ['parenthcos']
	},
	addresses_V: {
		label: t('Addresses'),
		section: 'addresses',
		kind: 'collection',
		collection: 'address',
		visibility: 'always',
		enumNames: [DcrFieldName.AddressesV],
		backendNames: ['addresses']
	},
	licenses_V: {
		label: t('Licenses'),
		section: 'licenses',
		kind: 'collection',
		collection: 'license',
		visibility: 'addable',
		enumNames: [DcrFieldName.LicensesV],
		backendNames: ['licenses']
	}
} as const satisfies Partial<Record<keyof DcrhcpInput, FieldDef>>;

export const HCP_UNSUPPORTED_INPUT_KEYS = [
	'vid_V',
	'spec1Local_VId',
	'spec2Local_VId',
	'URL1_V',
	'yearOfBirthCda_V'
] as const satisfies readonly (keyof DcrhcpInput)[];

export const HCP_HISTORIC_FIELDS: readonly HistoricFieldDef[] = [
	{
		names: ['hcpReasonStatus_V'],
		label: t('Status Reason'),
		anchor: 'hcpStatus_VId'
	},
	{
		names: ['spec1Local_V', 'specialty1_V'],
		label: t('Primary Local Specialty'),
		anchor: 'allSpecLocal_VId'
	},
	{
		names: ['spec2Local_V', 'specialty2_V'],
		label: t('Local Specialty {{n}}', { n: 2 }),
		anchor: 'allSpecLocal_VId'
	},
	...[3, 4, 5, 6, 7, 8, 9, 10].map((number): HistoricFieldDef => ({
		names: [`specialty${number}_V`],
		label: t('Local Specialty {{n}}', { n: number }),
		anchor: 'allSpecLocal_VId'
	})),
	{
		names: ['phoneCda_V'],
		label: t('Phone'),
		anchor: 'officePhoneCda_V',
		kind: 'phone'
	},
	{ names: ['jobType_V'], label: t('Job Type'), section: 'professional' },
	{
		names: ['certifiedTrainer_V'],
		label: t('Certified Trainings'),
		section: 'professional'
	},
	{ names: ['summary_V'], label: t('Summary'), section: 'professional' }
];

const getDistinctPhone2 = (profile: Hcp) => {
	const countryName = getPrimaryAddress(profile.addresses)?.countryCda_V;
	const phone2 = formatPhoneNumber(profile.phone2_V, countryName);
	const officePhone = formatPhoneNumber(profile.officePhoneCda_V, countryName);

	return phone2 && phone2 !== officePhone ? profile.phone2_V : null;
};

const getAffiliationCount = (profile: Hcp) =>
	profile.affiliationSearch?.__typename === 'HCPAffiliationsResults'
		? profile.affiliationSearch.paginationMetadata.total +
			(profile.affiliationSearch.inactiveCount ?? 0)
		: 0;

export const HCP_INLINE_LAYOUT: InlineLayout<Hcp, keyof typeof HCP_FIELDS> = {
	sections: [
		{
			section: 'name',
			entries: [
				inlineName(['firstNameCda_V', 'middleNameCda_V', 'lastNameCda_V'])
			]
		},
		{
			section: 'professional',
			entries: [
				inlineStatus(),
				inlineField('hcpTypeCda_VId', p => p.hcpType?.name),
				inlineField('hcpTypeLocal_VId', p => p.localHcpType?.name),
				inlineField('prefixCda_V', p => p.prefixCda_V),
				inlineField('suffixCda_V', p => p.suffixCda_V),
				inlineField('degree1Cda_VId', p => p.degree1Cda_V),
				inlineField('prescriberCda_V', p => p.prescriberCda_V),
				inlineField('allSpecLocal_VId', getLocalSpecialtyValue)
			]
		},
		{
			section: 'ids',
			entries: [
				inlineField('nhidCda_V', p => p.nhidCda_V),
				inlineField('nhid2_V', p => p.nhid2_V)
			]
		},
		{
			section: 'demographics',
			entries: [
				inlineField('languageCda_VId', p => p.languageCda_V),
				inlineField('gender_VId', p => validateNotUnknown(p.gender_V)),
				inlineField('birthYear_V', p => p.birthYear_V)
			]
		},
		{
			section: 'contact',
			entries: [
				inlineField('officePhoneCda_V', p => p.officePhoneCda_V),
				inlineField('phone2_V', getDistinctPhone2),
				inlineField('faxCda_V', p => p.faxCda_V),
				inlineEmails(t('Email')),
				inlineField('websiteCda_V', p => p.websiteCda_V)
			]
		},
		{
			section: 'affiliations',
			entries: [inlineCollection('parentHcos_V', getAffiliationCount)]
		},
		{
			section: 'addresses',
			entries: [inlineCollection('addresses_V', p => p.addresses?.length ?? 0)]
		},
		{
			section: 'licenses',
			entries: [inlineCollection('licenses_V', p => p.licenses?.length ?? 0)]
		}
	],
	suggested: [
		PROFILE_STATUS_FIELD_KEY,
		'allSpecLocal_VId',
		EMAILS_FIELD_NAME,
		'emailCda_V',
		NAME_GROUP_KEY
	]
};
